import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const CAT_ITEM_DEFINITIONS = [
  { shortLabel: 'Tosse', fullLabel: 'Tosse habitual' },
  { shortLabel: 'Catarro', fullLabel: 'Catarro / muco no peito' },
  { shortLabel: 'Pressão no peito', fullLabel: 'Sensação de pressão no peito' },
  { shortLabel: 'Falta de ar', fullLabel: 'Falta de ar ao subir ladeira ou escada' },
  { shortLabel: 'Limitação em casa', fullLabel: 'Limitação nas atividades em casa' },
  { shortLabel: 'Confiança para sair', fullLabel: 'Confiança para sair de casa' },
  { shortLabel: 'Sono', fullLabel: 'Qualidade do sono' },
  { shortLabel: 'Energia', fullLabel: 'Energia e disposição' },
] as const;

export const getCATSeverityLabel = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  if (total <= 10) return 'Impacto leve';
  if (total <= 20) return 'Impacto moderado';
  if (total <= 30) return 'Impacto elevado';
  return 'Impacto muito elevado';
};

export const getCATSeverityRangeLabel = (total?: number | null): string => {
  if (typeof total !== 'number') return 'Classificação indisponível';
  if (total <= 10) return 'Impacto Leve (0-10)';
  if (total <= 20) return 'Impacto Moderado (11-20)';
  if (total <= 30) return 'Impacto Elevado (21-30)';
  return 'Impacto Muito Elevado (31-40)';
};

export const getCATSeverityTone = (total?: number | null): string => {
  if (typeof total !== 'number') return 'bg-slate-100 text-slate-700';
  if (total <= 10) return 'bg-emerald-100 text-emerald-700';
  if (total <= 20) return 'bg-yellow-100 text-yellow-700';
  if (total <= 30) return 'bg-orange-100 text-orange-700';
  return 'bg-rose-100 text-rose-700';
};

export const getCATScoreColor = (total?: number | null): string => {
  if (typeof total !== 'number') return '#475569';
  if (total <= 10) return '#10b981';
  if (total <= 20) return '#f59e0b';
  if (total <= 30) return '#f97316';
  return '#f43f5e';
};

export const getCATInterpretationText = (total?: number | null): string => {
  if (typeof total !== 'number') {
    return 'Não há pontuação suficiente para interpretar o impacto clínico do CAT.';
  }
  if (total <= 10) {
    return 'A pontuação sugere baixo impacto sintomático da DPOC na vida diária, com menor carga funcional percebida.';
  }
  if (total <= 20) {
    return 'A pontuação sugere impacto moderado da DPOC, indicando sintomas relevantes e necessidade de seguimento clínico estruturado.';
  }
  if (total <= 30) {
    return 'A pontuação sugere impacto significativo da DPOC na vida diária. Requer atenção para carga sintomática elevada e limitações funcionais.';
  }
  return 'A pontuação sugere impacto muito elevado da DPOC, com alta carga de sintomas e importante comprometimento funcional percebido.';
};

export const getCATItemImpactLabel = (value?: number | null): string => {
  if (typeof value !== 'number') return 'Não informado';
  if (value <= 1) return 'Leve';
  if (value <= 3) return 'Moderado';
  return 'Grave';
};

export const getCATItemBadgeTone = (value?: number | null): string => {
  if (typeof value !== 'number') return 'bg-slate-100 text-slate-600';
  if (value <= 1) return 'bg-emerald-100 text-emerald-700';
  if (value <= 3) return 'bg-blue-100 text-blue-700';
  return 'bg-rose-100 text-rose-700';
};

interface CATResultRendererProps {
  payload: {
    answers?: unknown;
    total?: unknown;
  } | null | undefined;
  compact?: boolean;
}

export default function CATResultRenderer({ payload, compact = false }: CATResultRendererProps) {
  const answers = Array.isArray(payload?.answers) ? payload.answers : [];
  const total = typeof payload?.total === 'number' ? payload.total : null;
  const severity = getCATSeverityRangeLabel(total);
  const scoreColor = getCATScoreColor(total);
  const interpretation = resolveAssessmentInterpretationText(payload, getCATInterpretationText(total));

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Pontuação CAT"
        value={total ?? '—'}
        valueColor={scoreColor}
        valueCaption={typeof total === 'number' ? `${total}/40` : 'Pontuação indisponível'}
        classification={severity}
        interpretation={interpretation}
      >
          <div className="space-y-2">
            <div className="relative py-1">
              <div
                className="h-9 rounded-full"
                style={{
                  background:
                    'linear-gradient(90deg, #1cc8a0 0%, #9cd26e 32%, #f2d61a 52%, #ff9e44 74%, #f43f5e 100%)',
                }}
              />
              {typeof total === 'number' && (
                <div
                  className="absolute -top-2 h-[calc(100%+16px)] w-1 rounded-full bg-slate-800"
                  style={{ left: `calc(${(Math.max(0, Math.min(40, total)) / 40) * 100}% - 2px)` }}
                />
              )}
            </div>
            <div className="flex items-center justify-between text-xs font-medium text-slate-500">
              <span>0-10</span>
              <span>11-20</span>
              <span>21-30</span>
              <span>31-40</span>
            </div>
          </div>
      </QuestionnaireResultHero>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Item do questionário</th>
              <th className="px-6 py-4 text-center">Score</th>
              <th className="px-6 py-4">Impacto</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {CAT_ITEM_DEFINITIONS.map((item, index) => {
              const rawValue = answers[index];
              const value = typeof rawValue === 'number' && rawValue >= 0 ? rawValue : null;
              const impact = getCATItemImpactLabel(value);
              const tone = getCATItemBadgeTone(value);

              return (
                <tr key={item.fullLabel} className="hover:bg-slate-50/60">
                  <td className="px-6 py-4 font-bold text-slate-700">{compact ? item.shortLabel : item.fullLabel}</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">{value ?? '—'} / 5</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-lg px-2.5 py-1 text-[10px] font-black uppercase ${tone}`}>
                      {impact}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
